import { useState, useMemo } from 'react';
import { Copy, Calendar, AlertCircle } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { copyToClipboard } from '@/lib/utils';
import { toast } from 'sonner';

interface CronParsed {
  minute: string;
  hour: string;
  dom: string;
  month: string;
  dow: string;
}

const PRESETS = [
  { label: 'Every minute', expr: '* * * * *' },
  { label: 'Every 5 minutes', expr: '*/5 * * * *' },
  { label: 'Every hour', expr: '0 * * * *' },
  { label: 'Every 6 hours', expr: '0 */6 * * *' },
  { label: 'Every day at 9am', expr: '0 9 * * *' },
  { label: 'Every weekday at 9am', expr: '0 9 * * 1-5' },
  { label: 'First of month at midnight', expr: '0 0 1 * *' },
  { label: 'Sunday midnight', expr: '0 0 * * 0' },
];

const FIELD_LABELS = ['Minute', 'Hour', 'Day of month', 'Month', 'Day of week'];
const FIELD_RANGES = [
  { min: 0, max: 59 },
  { min: 0, max: 23 },
  { min: 1, max: 31 },
  { min: 1, max: 12 },
  { min: 0, max: 7 },
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function parseCron(expr: string): CronParsed | null {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  return {
    minute: parts[0],
    hour: parts[1],
    dom: parts[2],
    month: parts[3],
    dow: parts[4],
  };
}

function describe(parsed: CronParsed): string {
  function f(field: string, names?: string[]): string {
    if (field === '*') return 'every';
    if (field.startsWith('*/')) return `every ${field.slice(2)}`;
    if (field.includes('-')) return `from ${field.split('-')[0]} to ${field.split('-')[1]}`;
    if (field.includes(',')) return field.split(',').map((x) => names?.[parseInt(x)] ?? x).join(', ');
    if (names) {
      const n = parseInt(field);
      return names[n] ?? field;
    }
    return field;
  }

  const time =
    parsed.minute === '*' && parsed.hour === '*'
      ? 'Every minute'
      : parsed.minute === '0' && parsed.hour !== '*'
      ? `At ${parsed.hour.padStart(2, '0')}:00`
      : parsed.hour === '*'
      ? `At minute ${parsed.minute} of every hour`
      : `At ${parsed.hour.padStart(2, '0')}:${parsed.minute.padStart(2, '0')}`;

  const day = parsed.dom !== '*' ? `on day-of-month ${parsed.dom}` : '';
  const month = parsed.month !== '*' ? `in ${f(parsed.month, MONTHS)}` : '';
  const dow = parsed.dow !== '*' ? `on ${f(parsed.dow, DAYS)}` : '';

  return [time, day, month, dow].filter(Boolean).join(' ');
}

/** Compute the next N firings (UTC). */
function nextFirings(parsed: CronParsed, count: number): Date[] {
  const out: Date[] = [];
  const now = new Date();
  let cursor = new Date(now);
  cursor.setSeconds(0, 0);
  cursor.setMinutes(cursor.getMinutes() + 1);

  function matchField(value: number, field: string, range: { min: number; max: number }): boolean {
    if (field === '*') return true;
    return field.split(',').some((part) => {
      if (part.startsWith('*/')) {
        const step = parseInt(part.slice(2));
        return (value - range.min) % step === 0;
      }
      if (part.includes('-')) {
        const [a, b] = part.split('-').map(Number);
        return value >= a && value <= b;
      }
      return parseInt(part) === value;
    });
  }

  let safety = 0;
  while (out.length < count && safety < 50000) {
    safety++;
    const m = cursor.getMinutes();
    const h = cursor.getHours();
    const dom = cursor.getDate();
    const mon = cursor.getMonth() + 1;
    const dow = cursor.getDay();
    if (
      matchField(m, parsed.minute, FIELD_RANGES[0]) &&
      matchField(h, parsed.hour, FIELD_RANGES[1]) &&
      matchField(dom, parsed.dom, FIELD_RANGES[2]) &&
      matchField(mon, parsed.month, FIELD_RANGES[3]) &&
      matchField(dow === 7 ? 0 : dow, parsed.dow, FIELD_RANGES[4])
    ) {
      out.push(new Date(cursor));
    }
    cursor.setMinutes(cursor.getMinutes() + 1);
  }
  return out;
}

export function CronBuilder() {
  const [expr, setExpr] = useState('0 9 * * 1-5');

  const parsed = useMemo(() => parseCron(expr), [expr]);
  const description = parsed ? describe(parsed) : null;
  const next = useMemo(() => (parsed ? nextFirings(parsed, 5) : []), [parsed]);

  return (
    <div>
      <ToolHeader
        toolId="cron-builder"
        title="Cron expression builder"
        description="Compose, parse, and predict cron expressions. Plain-English explanation and the next 5 firings."
        badges={parsed ? <Badge variant="success">valid</Badge> : <Badge variant="danger">invalid</Badge>}
      />

      <Card className="mb-4">
        <Field label="Cron expression" hint="minute · hour · day-of-month · month · day-of-week">
          <Input
            value={expr}
            onChange={(e) => setExpr(e.target.value)}
            className="font-mono text-base !h-12"
            placeholder="0 9 * * 1-5"
          />
        </Field>
        {description && (
          <div className="mt-3 px-3 py-2 rounded-lg bg-accent-soft text-accent text-sm font-medium flex items-center gap-2">
            <Calendar size={12} />
            {description}
          </div>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {PRESETS.map((p) => (
            <button
              key={p.expr}
              onClick={() => setExpr(p.expr)}
              className="px-2.5 py-1 text-xs rounded-md border border-border hover:border-accent hover:bg-accent/5 transition"
            >
              {p.label}
            </button>
          ))}
          <Button
            size="sm"
            variant="ghost"
            onClick={async () => {
              const ok = await copyToClipboard(expr);
              if (ok) toast.success('Copied');
            }}
          >
            <Copy size={11} />
          </Button>
        </div>
      </Card>

      {parsed && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <div className="text-xs font-medium text-fg-muted mb-3">Fields</div>
            <div className="space-y-2 text-sm">
              {[
                { label: 'Minute', val: parsed.minute, range: '0-59' },
                { label: 'Hour', val: parsed.hour, range: '0-23' },
                { label: 'Day of month', val: parsed.dom, range: '1-31' },
                { label: 'Month', val: parsed.month, range: '1-12' },
                { label: 'Day of week', val: parsed.dow, range: '0-7 (Sun=0,7)' },
              ].map((r) => (
                <div key={r.label} className="flex items-center justify-between">
                  <span className="text-fg-muted">{r.label}</span>
                  <div className="flex items-center gap-2">
                    <code className="font-mono font-semibold text-accent">{r.val}</code>
                    <span className="text-2xs text-fg-subtle font-mono">{r.range}</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <div className="text-xs font-medium text-fg-muted mb-3">Next 5 firings (local time)</div>
            <div className="space-y-1.5 text-sm font-mono">
              {next.length === 0 ? (
                <div className="flex items-center gap-2 text-warning text-xs">
                  <AlertCircle size={12} />
                  No matching times in the next year.
                </div>
              ) : (
                next.map((d, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-fg-subtle">{d.toLocaleDateString(undefined, { weekday: 'short' })}</span>
                    <span className="tabular-nums">{d.toLocaleString()}</span>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

export default CronBuilder;
